import { useState } from "react";
import { FlaskConical, Loader2 } from "lucide-react";

import { CULQI_SANDBOX } from "@/constants";
import { authClient } from "@/lib/auth-client";

import { useBillingPlans } from "@/hooks/queries/useBillingPlans";
import { useMySubscription } from "@/hooks/queries/useMySubscription";
import { usePaymentHistory } from "@/hooks/queries/usePaymentHistory";
import { useViewQuota } from "@/hooks/queries/useViewQuota";
import {
  useCancelSubscription,
  useChangePlan,
  useDowngradeToFree,
  useSubscribe,
  useUpdateCard,
} from "@/hooks/mutations/useBillingMutations";
import { BillingConfirmDialog } from "@/components/billing/BillingConfirmDialog";
import { CurrentPlanPanel } from "@/components/billing/CurrentPlanPanel";
import { PaymentHistoryTable } from "@/components/billing/PaymentHistoryTable";
import { PlanCard } from "@/components/billing/PlanCard";
import { CorporatePlanBand } from "@/components/billing/CorporatePlanBand";
import { formatDate, formatPlanPrice, planDisplayName } from "@/lib/format";
import type { BillingPlan } from "@/types/domain";

/** Comercial: el plan que queremos empujar entre los proveedores, que son el foco. */
const RECOMMENDED_PLAN_ORDER = 2;

export default function PlanSettings() {
  const { data: session } = authClient.useSession();
  const { data: subscription, isLoading } = useMySubscription();
  const { data: plans } = useBillingPlans();
  const { data: quota } = useViewQuota();
  const { data: payments } = usePaymentHistory(Boolean(subscription));

  const subscribe = useSubscribe();
  const changePlan = useChangePlan();
  const cancelSubscription = useCancelSubscription();
  const updateCard = useUpdateCard();
  const downgradeToFree = useDowngradeToFree();

  // Plan pendiente de confirmar en el diálogo de cambio (null = diálogo cerrado).
  const [planToChange, setPlanToChange] = useState<BillingPlan | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [confirmingCardUpdate, setConfirmingCardUpdate] = useState(false);
  const [confirmingDowngrade, setConfirmingDowngrade] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const expired = Boolean(subscription?.isExpired);
  // Cambiar de plan y cancelar piden lo mismo: una suscripción viva en Culqi. Sin
  // ella (venció, ya la cancelaron o el plan lo puso un admin) se contrata de cero.
  const hasLiveSubscription = Boolean(
    subscription?.isManaged && !subscription.cancelAtPeriodEnd && !expired,
  );
  const busy =
    subscribe.isPending || changePlan.isPending || cancelSubscription.isPending;
  // En sandbox solo los admins pueden contratar (el backend lo rechaza con
  // PAYMENTS_DISABLED). Se refleja aquí para no pedirle la tarjeta a alguien que
  // va a recibir un rechazo.
  const isAdmin =
    (session?.user as { role?: string | null } | undefined)?.role === "admin";
  const paymentsDisabled = CULQI_SANDBOX && !isAdmin;

  function handleSelectPlan(plan: BillingPlan) {
    if (hasLiveSubscription) {
      setPlanToChange(plan);
      return;
    }
    subscribe.mutate(plan);
  }

  return (
    <div className="flex w-full flex-col gap-5">
      {CULQI_SANDBOX && isAdmin && (
        <div className="flex items-start gap-3 rounded-2xl border border-dashed border-amber-400 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950/30">
          <FlaskConical className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="text-sm text-amber-900 dark:text-amber-200">
            <p className="font-bold">Modo de pruebas de Culqi</p>
            <p className="mt-0.5">
              Ningún cobro es real y solo los administradores pueden contratar.
              Este aviso desaparece al activar las llaves de producción.
            </p>
          </div>
        </div>
      )}

      {subscription && (
        <CurrentPlanPanel
          subscription={subscription}
          quota={quota}
          expired={expired}
          onUpdateCard={() => setConfirmingCardUpdate(true)}
          onDowngradeToFree={() => setConfirmingDowngrade(true)}
          isUpdatingCard={updateCard.isPending}
        />
      )}

      <div>
        <h2 className="text-base font-extrabold text-slate-800 dark:text-slate-100">
          {subscription ? "Cambiar de plan" : "Elige tu plan"}
        </h2>
        <p className="mt-0.5 mb-5 text-sm text-slate-400 dark:text-slate-500">
          Facturación mensual. Puedes cancelar cuando quieras.
        </p>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {plans?.map((plan) => {
            const isCurrent = subscription?.planId === plan.plan_id && !expired;

            return (
              <PlanCard
                key={plan.plan_id}
                plan={plan}
                isCurrent={isCurrent}
                promoEndsAt={isCurrent ? subscription?.promoEndsAt : null}
                isRecommended={plan.plan_order === RECOMMENDED_PLAN_ORDER}
                disabled={busy}
                paymentsDisabled={paymentsDisabled}
                pending={
                  isCurrent
                    ? cancelSubscription.isPending
                    : subscribe.isPending &&
                      subscribe.variables?.plan_id === plan.plan_id
                }
                onSelect={() => handleSelectPlan(plan)}
                onCancel={
                  isCurrent && hasLiveSubscription
                    ? () => setConfirmingCancel(true)
                    : undefined
                }
              />
            );
          })}
        </div>

        <CorporatePlanBand />
      </div>

      {subscription && <PaymentHistoryTable payments={payments ?? []} />}

      <BillingConfirmDialog
        open={planToChange !== null}
        onOpenChange={(open) => !open && setPlanToChange(null)}
        title={`Cambiar al plan ${planToChange ? planDisplayName(planToChange) : ""}`}
        description={
          <>
            Se te cobrará{" "}
            <strong>
              {formatPlanPrice(
                planToChange?.plan_promoamount ?? planToChange?.plan_priceamount,
                planToChange?.plan_currency,
              )}
            </strong>{" "}
            hoy y tu ciclo de facturación se reinicia
            {planToChange?.plan_promocycles ? (
              <>
                , con el precio de lanzamiento durante{" "}
                {planToChange.plan_promocycles} meses y{" "}
                {formatPlanPrice(
                  planToChange.plan_priceamount,
                  planToChange.plan_currency,
                )}
                /mes después
              </>
            ) : null}
            . No se prorratea lo que quede del periodo actual.
          </>
        }
        confirmLabel="Confirmar cambio"
        pending={changePlan.isPending}
        onConfirm={() => {
          if (!planToChange) return;
          changePlan.mutate(planToChange.plan_id, {
            onSettled: () => setPlanToChange(null),
          });
        }}
      />

      <BillingConfirmDialog
        open={confirmingCardUpdate}
        onOpenChange={setConfirmingCardUpdate}
        title="Actualizar tu tarjeta"
        description={
          <>
            Para comprobar que la tarjeta funciona, Culqi le hace un cargo de{" "}
            <strong>S/ 3.00</strong> y lo <strong>devuelve al instante</strong>.
            Puede aparecer un par de días como retención en tu estado de cuenta.
            Por eso la ventana de Culqi dice "Pagar S/ 3.00": es su etiqueta, no un
            cobro nuestro. Tu plan y su precio no cambian.
          </>
        }
        confirmLabel="Continuar"
        pending={updateCard.isPending}
        onConfirm={() => {
          // La ventana de Culqi se monta fuera del diálogo: si este sigue abierto,
          // su focus trap y el `pointer-events: none` del body la dejan inservible.
          setConfirmingCardUpdate(false);
          updateCard.mutate();
        }}
      />

      <BillingConfirmDialog
        open={confirmingDowngrade}
        onOpenChange={setConfirmingDowngrade}
        title="Pasar al plan Free"
        description={
          <>
            Dejamos de intentar el cobro y tu cuenta pasa al plan Free ahora mismo,
            con menos oportunidades y líneas de negocio. No perderás nada de lo que
            ya desbloqueaste, y puedes volver a contratar cuando quieras.
          </>
        }
        confirmLabel="Pasar a Free"
        destructive
        pending={downgradeToFree.isPending}
        onConfirm={() =>
          downgradeToFree.mutate(undefined, {
            onSettled: () => setConfirmingDowngrade(false),
          })
        }
      />

      <BillingConfirmDialog
        open={confirmingCancel}
        onOpenChange={setConfirmingCancel}
        title="Cancelar tu suscripción"
        description={
          <>
            No volveremos a cobrarte. Mantienes tu plan actual hasta el{" "}
            <strong>{formatDate(subscription?.expiresat)}</strong> y después tu
            cuenta pasa al plan Free, con menos oportunidades y líneas de
            negocio.
          </>
        }
        confirmLabel="Sí, cancelar"
        destructive
        pending={cancelSubscription.isPending}
        onConfirm={() =>
          cancelSubscription.mutate(undefined, {
            onSettled: () => setConfirmingCancel(false),
          })
        }
      />
    </div>
  );
}
